import * as Sentry from "@sentry/react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import type { User } from "schema/user";

declare global {
  interface Window {
    __APP_CONFIG__?: {
      LOGIN_URL?: string;
    };
  }
}

function getLoginUrl(): string {
  return window.__APP_CONFIG__?.LOGIN_URL || "/login";
}

function redirectToLogin(): void {
  const loginUrl = new URL(getLoginUrl(), window.location.origin);
  loginUrl.searchParams.set("next", window.location.href);

  const referralCode = localStorage.getItem("referral_code");
  if (referralCode) {
    loginUrl.searchParams.set("referral", referralCode);
  }

  window.location.href = loginUrl.toString();
}

const useAuthValue = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Sentry.setUser(
      user && {
        id: user.uid,
        email: user.email,
        username: user.name,
        isAdmin: user.isAdmin || false,
      },
    );
  }, [user]);

  const checkAuth = useCallback(async () => {
    const resp = await fetch("/api/me", { credentials: "include" });
    if (resp.ok) {
      const userData = await resp.json();
      setUser(userData);
    } else {
      setUser(null);
      redirectToLogin();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleSessionExpired = useCallback(() => {
    redirectToLogin();
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/logout", {
      method: "POST",
      credentials: "include",
    });
    Sentry.setUser(null);
    redirectToLogin();
  }, []);

  if (loading || !user) {
    return null;
  }

  return { user, handleSessionExpired, logout, loading };
};

type AuthContextValue = ReturnType<typeof useAuthValue>;

const AuthContext = createContext<AuthContextValue>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthValue();
  if (!value) return null;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
